import ControlPanelController from "./control_panel_controller"

/**
 * Stimulus controller for Development sub-tab CRUD.
 * Handles key dates, phase statuses, and dev punchlist kinds.
 */
export default class extends ControlPanelController {
  static targets = [
    "newName", "list",
    "searchInput",
    "createModal", "createName",
    "editModal", "editId", "editName",
    "deleteConfirmModal", "deleteId"
  ]

  static values = {
    addUrl: String,
    renameUrl: String,
    destroyUrl: String,
    toggleUrl: String,
    uploadsToggleUrl: String,
    foldersToggleUrl: String
  }

  // ── Search (client-side filter) ───────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim().toLowerCase() : ""
      const rows = this.element.querySelectorAll("tbody tr")
      rows.forEach(row => {
        const text = row.textContent.toLowerCase()
        row.style.display = !q || text.includes(q) ? "" : "none"
      })
    }, 200)
  }

  // ── Create (modal-based) ────────────────────────────────────────

  async submitCreate() {
    if (!this.hasCreateNameTarget) return
    const name = this.createNameTarget.value.trim()
    if (!name) return

    try {
      const response = await fetch(this.addUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ name })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeCreateModal()
        this._insertRow(data.id, data.name)
        this._restripe()
        this._showToast("Created successfully")
      } else {
        this._showToast(data.message || "Error creating item", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Add item (inline, used by phase_status) ─────────────────────

  addItem() {
    if (!this.hasNewNameTarget) return
    const name = this.newNameTarget.value.trim()
    if (!name) return

    this._post(this.addUrlValue, { name }).then(ok => {
      if (ok) {
        this.newNameTarget.value = ""
        this._reload()
      }
    })
  }

  addOnEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.addItem()
    }
  }

  // ── Rename item (inline, used by phase_status) ──────────────────

  rename(event) {
    const row = event.currentTarget.closest("[data-line-id]")
    if (!row) return
    const lineId = row.dataset.lineId
    const input = row.querySelector("[data-name-input]")
    if (!input) return
    const name = input.value.trim()
    if (!name) return

    this._post(this.renameUrlValue, { line_id: lineId, name }).then(ok => {
      if (ok) this._showToast("Updated successfully")
    })
  }

  renameOnEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.rename(event)
    }
  }

  // ── Rename (modal-based, from kebab menu) ───────────────────────

  async submitRename() {
    if (!this.hasEditIdTarget || !this.hasEditNameTarget) return
    const lineId = this.editIdTarget.value
    const name = this.editNameTarget.value.trim()
    if (!lineId || !name) return

    try {
      const response = await fetch(this.renameUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ line_id: lineId, name })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditModal()
        const row = this.element.querySelector(`tbody tr[data-id="${lineId}"]`)
        if (row) {
          const nameCell = row.querySelector("td")
          if (nameCell) nameCell.textContent = data.name || name
          const editBtn = row.querySelector('[data-action*="openEditModal"]')
          if (editBtn) editBtn.dataset.name = data.name || name
        }
        this._showToast("Updated successfully")
      } else {
        this._showToast(data.message || "Error renaming item", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Delete item ───────────────────────────────────────────────────

  destroyItem(event) {
    const lineId = event.currentTarget.dataset.lineId
    if (!lineId) return

    this._delete(this.destroyUrlValue, { line_id: lineId }).then(ok => {
      if (ok) this._reload()
    })
  }

  async submitDelete() {
    if (!this.hasDeleteIdTarget) return
    const lineId = this.deleteIdTarget.value
    if (!lineId) return

    try {
      const response = await fetch(this.destroyUrlValue, {
        method: "DELETE",
        headers: this._headers(),
        body: JSON.stringify({ line_id: lineId })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteConfirm()
        const row = this.element.querySelector(`tbody tr[data-id="${lineId}"]`)
        if (row) row.remove()
        this._restripe()
        this._showToast(data.message || "Deleted successfully")
      } else {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Cannot delete item", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Toggle (auto-completion) ──────────────────────────────────────

  async toggle(event) {
    if (!this.hasToggleUrlValue) return
    const value = event.currentTarget.checked
    try {
      await fetch(this.toggleUrlValue, {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ automatic_completion_previous_step: value })
      })
      this._showToast("Updated successfully")
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // "Allow uploads to completed steps" (Phelipe 2026-08-27) — same shape as
  // #toggle, its own endpoint and setting.
  async toggleCompletedUploads(event) {
    if (!this.hasUploadsToggleUrlValue) return
    const value = event.currentTarget.checked
    try {
      await fetch(this.uploadsToggleUrlValue, {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ allow_completed_step_uploads: value })
      })
      this._showToast("Updated successfully")
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async toggleCompletedFolders(event) {
    if (!this.hasFoldersToggleUrlValue) return
    const value = event.currentTarget.checked
    try {
      await fetch(this.foldersToggleUrlValue, {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ allow_completed_step_folders: value })
      })
      this._showToast("Updated successfully")
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── DOM helpers ─────────────────────────────────────────────────

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;")
  }

  _insertRow(id, name) {
    const tbody = this.element.querySelector("tbody")
    if (!tbody) return
    const escapedName = this._esc(name)
    const colCount = this.element.querySelectorAll("thead th").length
    const countTd = colCount >= 3 ? `<td class="px-3 py-1.5 text-[13px] text-gray-500 text-right">0</td>` : ""

    const html = `<tr data-id="${id}" class="bg-white border-b border-gray-100 hover:bg-brand/[0.04]">
      <td class="px-3 py-1.5 text-[13px] text-gray-900 font-medium">${escapedName}</td>
      ${countTd}
      <td class="px-3 py-1.5 text-right">
        <div class="relative" data-controller="dropdown">
          <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer" data-action="click->dropdown#toggle">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
          </button>
          <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-32" data-dropdown-target="menu">
            <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer" data-action="click->cpanel-development#openEditModal" data-id="${id}" data-name="${escapedName}">Rename</button>
            <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer" data-action="click->cpanel-development#openDeleteConfirm" data-id="${id}">Remove</button>
          </div>
        </div>
      </td>
    </tr>`

    tbody.insertAdjacentHTML("beforeend", html)
  }

  _restripe() {
    const rows = this.element.querySelectorAll("tbody tr")
    rows.forEach((row, i) => {
      row.classList.remove("bg-white", "bg-gray-50/60")
      row.classList.add(i % 2 ? "bg-gray-50/60" : "bg-white")
    })
  }

  // ── Helpers ───────────────────────────────────────────────────────

  async _post(url, body) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      return response.ok
    } catch {
      this._showToast("Network error", "error")
      return false
    }
  }

  async _delete(url, body) {
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      return response.ok
    } catch {
      this._showToast("Network error", "error")
      return false
    }
  }
}
